import breakpoints from "./breakpoints";
import components from "./components";
import shadows from "./shadows";
import typography from "./typography";
import variants from "./variants";
import "@mui/lab/themeAugmentation";
import { grey } from "@mui/material/colors";
import { alpha, createTheme as createMuiTheme } from "@mui/material/styles";

const buildTextPalette = (palette) => ({
  primary:
    palette.text?.primary ||
    (palette.mode === "dark" ? alpha("#fff", 0.95) : alpha(grey[900], 0.92)),
  secondary:
    palette.text?.secondary ||
    (palette.mode === "dark" ? alpha("#fff", 0.7) : alpha(grey[900], 0.68)),
});

const normalizeThemeConfig = (themeConfig) => {
  const text = buildTextPalette(themeConfig.palette);
  const divider =
    themeConfig.palette.divider ||
    (themeConfig.palette.mode === "dark"
      ? alpha("#fff", 0.12)
      : alpha(grey[900], 0.12));

  const appBarBackground =
    themeConfig.header?.background || themeConfig.palette.background.paper;
  const appBarColor = themeConfig.header?.color || text.primary;
  const sidebarBackground =
    themeConfig.sidebar?.background || themeConfig.palette.background.paper;
  const sidebarColor = themeConfig.sidebar?.color || text.primary;
  const sidebarHeaderBackground =
    themeConfig.sidebar?.header?.background || sidebarBackground;
  const sidebarFooterBackground =
    themeConfig.sidebar?.footer?.background || sidebarBackground;

  return {
    ...themeConfig,
    palette: {
      ...themeConfig.palette,
      divider,
      text,
      appBar: {
        background: appBarBackground,
        color: appBarColor,
      },
      dashboard: {
        axis:
          themeConfig.palette.dashboard?.axis ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.38)
            : alpha(grey[900], 0.38)),
        border: themeConfig.palette.dashboard?.border || divider,
        emptyState: themeConfig.palette.dashboard?.emptyState || text.secondary,
        gaugeTrack:
          themeConfig.palette.dashboard?.gaugeTrack ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.16)
            : alpha(grey[900], 0.16)),
        grid:
          themeConfig.palette.dashboard?.grid ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.14)
            : alpha(grey[900], 0.12)),
        hover:
          themeConfig.palette.dashboard?.hover ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.08)
            : alpha(themeConfig.palette.primary.main, 0.08)),
        inputBackground:
          themeConfig.palette.dashboard?.inputBackground ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.03)
            : grey[50]),
        inputBorder: themeConfig.palette.dashboard?.inputBorder || divider,
        markerLabel:
          themeConfig.palette.dashboard?.markerLabel ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.58)
            : alpha(grey[900], 0.5)),
        selected:
          themeConfig.palette.dashboard?.selected ||
          (themeConfig.palette.mode === "dark"
            ? alpha(themeConfig.palette.primary.main, 0.18)
            : alpha(themeConfig.palette.primary.main, 0.12)),
        surface:
          themeConfig.palette.dashboard?.surface ||
          themeConfig.palette.background.paper,
        surfaceAlt:
          themeConfig.palette.dashboard?.surfaceAlt ||
          (themeConfig.palette.mode === "dark"
            ? alpha("#fff", 0.04)
            : grey[50]),
        text: themeConfig.palette.dashboard?.text || text.primary,
        textMuted: themeConfig.palette.dashboard?.textMuted || text.secondary,
      },
      footer: {
        background:
          themeConfig.footer?.background ||
          themeConfig.palette.background.paper,
        color: themeConfig.footer?.color || text.secondary,
        link: themeConfig.palette.primary.main,
      },
      sidebar: {
        activeBackground:
          themeConfig.sidebar?.activeBackground || sidebarHeaderBackground,
        background: sidebarBackground,
        border: themeConfig.sidebar?.border || divider,
        color: sidebarColor,
        footerBackground: sidebarFooterBackground,
        footerColor: themeConfig.sidebar?.footer?.color || sidebarColor,
        headerBackground: sidebarHeaderBackground,
        headerColor: themeConfig.sidebar?.header?.color || sidebarColor,
        hoverBackground:
          themeConfig.sidebar?.hoverBackground ||
          alpha(
            sidebarColor,
            themeConfig.palette.mode === "dark" ? 0.08 : 0.06
          ),
        mutedColor: themeConfig.sidebar?.mutedColor || alpha(sidebarColor, 0.7),
        sectionTitleColor:
          themeConfig.sidebar?.sectionTitleColor || alpha(sidebarColor, 0.4),
        brandColor: themeConfig.sidebar?.header?.brand?.color || appBarColor,
      },
    },
  };
};

const createTheme = (name) => {
  let themeConfig = variants.find((variant) => variant.name === name);

  if (!themeConfig) {
    console.warn(new Error(`The theme ${name} is not valid`));
    themeConfig = variants[0];
  }

  const normalizedThemeConfig = normalizeThemeConfig(themeConfig);

  return createMuiTheme({
    breakpoints: breakpoints,
    components: components,
    palette: normalizedThemeConfig.palette,
    shadows: shadows,
    shape: {
      borderRadius: 6,
    },
    spacing: 4,
    typography: typography,
  });
};

export default createTheme;
